<div class="k-d-flex k-justify-content-between k-gap-md">
  <!-- The popup anchor sits on a plain element: a template reference on kendo-badge-container
       resolves to the component, and kendo-popup only accepts an ElementRef or an HTMLElement. -->
  <span #syncStatusAnchor>
    <kendo-badge-container>
      <kendo-chip themeColor="success" (click)="onSyncStatusClick()">
        <span class="custom-content">
          <span *ngIf="alerts">
            {{ alerts.scheduledChangesCount }} scheduled change(s)
          </span>
          <kendo-loader
            size="small"
            themeColor="success"
            *ngIf="!alerts"
          ></kendo-loader>
        </span>
      </kendo-chip>
      <kendo-badge
        *ngIf="failedSyncCount > 0"
        size="small"
        rounded="full"
        [align]="{ vertical: 'top', horizontal: 'end' }"
        themeColor="error"
        >{{ failedSyncCount }}</kendo-badge
      >
    </kendo-badge-container>
  </span>

  <kendo-chip
    *ngIf="alertCount > 0"
    themeColor="warning"
    (click)="alertsDropdownVisible = !alertsDropdownVisible"
    #alertsAnchor
  >
    <span class="custom-content">
      <fa-icon [icon]="['fas', 'exclamation-triangle']" style="margin-right: 4px;"></fa-icon>
      <span>Alerts ({{ alertCount }})</span>
    </span>
  </kendo-chip>
</div>

<!-- Sync status dropdown -->
<kendo-popup
  *ngIf="syncStatusDropdownVisible"
  [anchor]="syncStatusAnchor"
  [animate]="true"
  (anchorViewportLeave)="syncStatusDropdownVisible = false"
>
  <div class="sync-status-dropdown">
    <div class="sync-status-dropdown__title">Sync status</div>
    <div class="sync-status-dropdown-item">
      <fa-icon [icon]="['fas', 'info-circle']" class="icon-info"></fa-icon>
      <span>{{ alerts?.scheduledChangesCount }} scheduled change(s)</span>
    </div>
    <div
      *ngIf="failedSyncCount > 0"
      class="sync-status-dropdown-item sync-status-dropdown-item--error"
    >
      <fa-icon [icon]="['fas', 'exclamation-circle']" class="icon-error"></fa-icon>
      <div class="sync-status-dropdown-item__body">
        <span>{{ failedSyncCount }} sync(s) need review</span>
        <a
          href="javascript:void(0)"
          class="sync-status-dropdown-item__link"
          (click)="navigateToFailedSyncs(); syncStatusDropdownVisible = false"
          >View failures in History &rarr;</a
        >
      </div>
    </div>
  </div>
</kendo-popup>

<!-- Alerts dropdown -->
<kendo-popup
  *ngIf="alertsDropdownVisible"
  [anchor]="alertsAnchor"
  [animate]="true"
  (anchorViewportLeave)="alertsDropdownVisible = false"
>
  <div class="alerts-dropdown">
    <div
      *ngIf="(alerts?.newLtoCount ?? 0) > 0 && !isAvailabilityOnly"
      class="alerts-dropdown-item alerts-dropdown-item--error"
      (click)="navigateToLtos(); alertsDropdownVisible = false"
    >
      <fa-icon [icon]="['fas', 'exclamation-circle']" class="icon-error"></fa-icon>
      <span>{{ alerts?.newLtoCount }} new LTO item(s)</span>
    </div>
    <div
      *ngIf="(alerts?.zeroPriceCount ?? 0) > 0"
      class="alerts-dropdown-item alerts-dropdown-item--warning"
      (click)="navigateToZeroPriceReport(); alertsDropdownVisible = false"
    >
      <fa-icon [icon]="['fas', 'exclamation-triangle']" class="icon-warning"></fa-icon>
      <span>{{ alerts?.zeroPriceCount }} zero price item(s)</span>
    </div>
  </div>
</kendo-popup>

<!-- Combined alerts popup (on login) -->
<kendo-dialog
  [width]="600"
  *ngIf="alertsPopupVisible"
  (close)="alertsPopupVisible = false"
>
  <kendo-dialog-titlebar>
    <fa-icon [icon]="['fas', 'exclamation-triangle']" class="k-mr-2"></fa-icon>
    <span>You have alerts!</span>
  </kendo-dialog-titlebar>

  <div class="alerts-cards">
    <!-- LTO card -->
    <div
      *ngIf="(alerts?.newLtoCount ?? 0) > 0 && !isAvailabilityOnly"
      class="alert-card alert-card--error"
    >
      <div class="alert-card__header">
        <fa-icon [icon]="['fas', 'exclamation-circle']" class="icon-error" size="lg"></fa-icon>
        <span class="alert-card__title">New LTO Items</span>
      </div>
      <p class="alert-card__description">
        {{ alerts?.newLtoCount }} new LTO item(s) need to be reviewed.
      </p>
      <ul class="alert-card__lto-list" *ngIf="newLtos.length > 0">
        <li *ngFor="let lto of newLtos">
          <span>{{ lto.itemName }} ({{ lto.parItemId }})</span>
          <span *ngFor="let docPath of lto.documentPath">
            <a href="javascript:void(0)" (click)="downloadDocument(lto, docPath)" kendoTooltip title="Download document" class="alert-card__doc-link">
              <fa-icon [icon]="['fas', 'paperclip']" class="k-mr-1"></fa-icon>{{ docPath }}
            </a>
          </span>
        </li>
      </ul>
      <div class="alert-card__actions">
        <button kendoButton themeColor="primary" (click)="viewLtoAlert()">View</button>
      </div>
    </div>

    <!-- Zero Price card -->
    <div
      *ngIf="(alerts?.zeroPriceCount ?? 0) > 0"
      class="alert-card alert-card--warning"
    >
      <div class="alert-card__header">
        <fa-icon [icon]="['fas', 'exclamation-triangle']" class="icon-warning" size="lg"></fa-icon>
        <span class="alert-card__title">Zero Price Items</span>
      </div>
      <p class="alert-card__description">
        {{ alerts?.zeroPriceCount }} active item(s) are currently priced at $0:
      </p>
      <ul class="alert-card__zero-price-breakdown">
        <li *ngIf="(alerts?.zeroPriceALaCarteCount ?? 0) > 0">
          <strong>A La Carte:</strong> {{ alerts?.zeroPriceALaCarteCount }} item(s)
        </li>
        <li *ngIf="(alerts?.zeroPriceComboCount ?? 0) > 0">
          <strong>Combo:</strong> {{ alerts?.zeroPriceComboCount }} item(s)
        </li>
        <li *ngIf="(alerts?.zeroPriceModifierCount ?? 0) > 0">
          <strong>Modifier:</strong> {{ alerts?.zeroPriceModifierCount }} item(s)
        </li>
      </ul>
      <div class="alert-card__actions">
        <button kendoButton themeColor="primary" (click)="viewZeroPriceAlert()">View</button>
      </div>
    </div>

    <!-- Sync failures card -->
    <div
      *ngIf="failedSyncCount > 0"
      class="alert-card alert-card--error"
    >
      <div class="alert-card__header">
        <fa-icon [icon]="['fas', 'exclamation-circle']" class="icon-error" size="lg"></fa-icon>
        <span class="alert-card__title">Sync Failures</span>
      </div>
      <p class="alert-card__description">
        {{ failedSyncCount }} sync(s) need review.
      </p>
      <div class="alert-card__actions">
        <button kendoButton themeColor="primary" (click)="viewFailedSyncAlert()">View</button>
      </div>
    </div>
  </div>
</kendo-dialog>

<!-- LTO Acknowledgement dialog -->
<kendo-dialog
  [width]="700"
  *ngIf="acknowledgementVisible"
  (close)="acknowledgementVisible = false"
>
  <kendo-dialog-titlebar>
    <span>New LTO item(s) review complete</span>
  </kendo-dialog-titlebar>

  <p class="k-font-size-lg">
    If you continue, you will no longer be alerted for these item(s).
  </p>

  <ul class="k-font-size-lg">
    <li>
      They'll remain available in <strong>Product Pricing</strong> under the
      <strong>LTO</strong> category.
    </li>
    <li>You won't see this alert again until new LTO items are added.</li>
  </ul>
  <kendo-dialog-actions layout="end">
    <button kendoButton (click)="acknowledgementVisible = false">
      No, keep reminding me
    </button>
    <button
      kendoButton
      (click)="acknowledgeLtos(); acknowledgementVisible = false"
      themeColor="success"
    >
      Yes, I am done
    </button>
  </kendo-dialog-actions>
</kendo-dialog>


<!-- Sync failure acknowledgement dialog -->
<kendo-dialog
  [width]="700"
  *ngIf="syncAcknowledgementVisible"
  (close)="syncAcknowledgementVisible = false"
>
  <kendo-dialog-titlebar>
    <span>Sync failure review complete</span>
  </kendo-dialog-titlebar>

  <p class="k-font-size-lg">
    If you continue, you will no longer be alerted for these sync(s).
  </p>

  <ul class="k-font-size-lg">
    <li>
      They'll remain available in <strong>Reports</strong> under
      <strong>History</strong>.
    </li>
    <li>You won't see this alert again until a new sync fails.</li>
  </ul>
  <kendo-dialog-actions layout="end">
    <button kendoButton (click)="syncAcknowledgementVisible = false">
      No, keep reminding me
    </button>
    <button
      kendoButton
      (click)="acknowledgeSyncFailures()"
      themeColor="success"
    >
      Yes, I am done
    </button>
  </kendo-dialog-actions>
</kendo-dialog>
